/**
 * Theme resolution, as pure functions.
 *
 * `system` is the default and is stored as the *absence* of a key: a visitor who
 * has never touched the toggle keeps following their OS, including when they change
 * it mid-session. Only an explicit choice is persisted.
 */

/** What the user asked for. */
export type ThemePref = 'system' | 'light' | 'dark';
/** What actually gets painted. `system` has been resolved away. */
export type Theme = 'light' | 'dark';

export const THEME_KEY = 'sag.theme';
/** The attribute `main.css` keys its dark block on. Also stamped by the inline boot script. */
export const THEME_ATTR = 'data-theme';

type Reader = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** `localStorage` is absent in unit tests and throws in some private-browsing modes. */
function storage(): Reader | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

/** Anything unrecognised — a stale key, a hand-edited value — falls back to `system`. */
export function readPref(store: Reader | null = storage()): ThemePref {
  try {
    const stored = store?.getItem(THEME_KEY);
    return isTheme(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function writePref(pref: ThemePref, store: Reader | null = storage()): void {
  try {
    // Storing "system" would freeze the choice; removing it is what keeps following the OS.
    if (pref === 'system') store?.removeItem(THEME_KEY);
    else store?.setItem(THEME_KEY, pref);
  } catch {
    // A full or blocked quota costs the preference, not the page.
  }
}

export function resolveTheme(pref: ThemePref, systemPrefersDark: boolean): Theme {
  if (pref !== 'system') return pref;
  return systemPrefersDark ? 'dark' : 'light';
}
